import React, { useState, useRef } from 'react';
import './PhotoUploadWidget.css';

/**
 * PhotoUploadWidget - Drag & drop photo upload for coach notes
 *
 * Props:
 * - onPhotosAdded: callback with array of uploaded photo objects
 * - onPhotoRemoved: callback with photo ID when removed
 * - maxPhotos: max number of photos (default: 5)
 * - maxFileSize: max file size in MB (default: 10)
 */
function PhotoUploadWidget({
  onPhotosAdded,
  onPhotoRemoved,
  maxPhotos = 5,
  maxFileSize = 10,
  existingPhotos = []
}) {
  const [photos, setPhotos] = useState(existingPhotos);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const allowedMimes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  /**
   * Validate photo file
   */
  const validateFile = (file) => {
    if (!allowedMimes.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type: ${file.type}. Use JPG, PNG, WebP, or GIF.`
      };
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxFileSize) {
      return {
        valid: false,
        error: `File size ${sizeMB.toFixed(2)}MB exceeds ${maxFileSize}MB limit`
      };
    }

    return { valid: true };
  };

  /**
   * Handle file selection (from input or drop)
   */
  const handleFiles = async (files) => {
    const fileArray = Array.from(files);

    // Check max photos limit
    if (photos.length + fileArray.length > maxPhotos) {
      setError(`Maximum ${maxPhotos} photos per note`);
      return;
    }

    // Validate all files first
    for (let file of fileArray) {
      const validation = validateFile(file);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
    }

    setError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      const newPhotos = [];

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];

        // Create preview
        const reader = new FileReader();
        const photoData = await new Promise((resolve) => {
          reader.onload = (e) => {
            const photoObj = {
              id: `preview_${Date.now()}_${i}`,
              file: file,
              preview: e.target.result,
              fileName: file.name,
              uploadStatus: 'pending', // pending, uploading, completed, error
              caption: null,
              cloudinaryUrl: null,
              thumbnailUrl: null
            };
            resolve(photoObj);
          };
          reader.readAsDataURL(file);
        });

        newPhotos.push(photoData);
        setUploadProgress(Math.round(((i + 1) / fileArray.length) * 100));
      }

      const updatedPhotos = [...photos, ...newPhotos];
      setPhotos(updatedPhotos);

      // Notify parent that photos were added to preview
      if (onPhotosAdded) {
        onPhotosAdded(updatedPhotos);
      }

      setUploadProgress(0);
    } catch (err) {
      setError(`Error processing files: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle drag over
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  /**
   * Handle drop
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  /**
   * Handle file input change
   */
  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  /**
   * Remove photo from preview
   */
  const removePhoto = (photoId) => {
    const filtered = photos.filter((p) => p.id !== photoId);
    setPhotos(filtered);

    if (onPhotoRemoved) {
      onPhotoRemoved(photoId);
    }

    if (onPhotosAdded) {
      onPhotosAdded(filtered);
    }
  };

  /**
   * Update photo caption
   */
  const updateCaption = (photoId, caption) => {
    const updated = photos.map((p) =>
      p.id === photoId ? { ...p, caption } : p
    );
    setPhotos(updated);

    if (onPhotosAdded) {
      onPhotosAdded(updated);
    }
  };

  /**
   * Trigger file input click
   */
  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="photo-upload-widget">
      {/* Upload Area */}
      {photos.length < maxPhotos && (
        <div
          className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClickUpload}
        >
          <div className="upload-icon">📸</div>
          <div className="upload-text">
            <p className="upload-main">Drag photos here or click to browse</p>
            <p className="upload-sub">
              JPG, PNG, WebP, GIF up to {maxFileSize}MB ({photos.length}/{maxPhotos})
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedMimes.join(',')}
            onChange={handleChange}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="photo-error">
          <span className="error-icon">⚠️</span>
          <span className="error-text">{error}</span>
          <button className="error-dismiss" onClick={() => setError(null)}>
            ✕
          </button>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
          </div>
          <span className="progress-text">{uploadProgress}%</span>
        </div>
      )}

      {/* Photo Previews */}
      {photos.length > 0 && (
        <div className="photo-previews">
          <h4 className="previews-header">📸 Photos ({photos.length}/{maxPhotos})</h4>
          <div className="preview-grid">
            {photos.map((photo) => (
              <div key={photo.id} className="preview-card">
                {/* Preview Image */}
                <div className="preview-image-wrapper">
                  <img
                    src={photo.preview || photo.thumbnailUrl}
                    alt={photo.fileName}
                    className="preview-image"
                  />
                  {photo.uploadStatus === 'uploading' && (
                    <div className="upload-overlay">
                      <div className="spinner" />
                    </div>
                  )}
                </div>

                {/* File Info */}
                <div className="preview-info">
                  <p className="preview-name" title={photo.fileName}>
                    {photo.fileName}
                  </p>
                </div>

                {/* Caption Input */}
                <input
                  type="text"
                  placeholder="Add caption..."
                  value={photo.caption || ''}
                  onChange={(e) => updateCaption(photo.id, e.target.value)}
                  className="preview-caption"
                  maxLength={100}
                />

                {/* Remove Button */}
                <button
                  className="preview-remove"
                  onClick={() => removePhoto(photo.id)}
                  title="Remove photo"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotoUploadWidget;
